import React, { createContext, useContext } from "react";
import { motion } from "framer-motion";
import { primaryButton, textButton } from "./ui";

// 1 when moving forward through the steps, -1 when going back.
export const Direction = createContext(1);

const EASE_OUT = [0.22, 1, 0.36, 1];

// Exits read `custom` from AnimatePresence, so they slide the new direction.
const slide = {
  enter: (dir) => ({ opacity: 0, x: 32 * dir }),
  center: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.45,
      ease: EASE_OUT,
      staggerChildren: 0.06,
      delayChildren: 0.04,
    },
  },
  exit: (dir) => ({
    opacity: 0,
    x: -24 * dir,
    transition: { duration: 0.22, ease: [0.4, 0, 1, 1] },
  }),
};

const rise = {
  enter: { opacity: 0, y: 10 },
  center: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE_OUT } },
};

// Opacity only: a transform here would unpin the fixed footer.
const fade = {
  enter: { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.3, delay: 0.12 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

// Room under the content for the fixed footer, so nothing scrolls out of
// reach behind it: each line above the button adds about 28px.
const FOOTER_CLEARANCE = {
  "false-false": "pb-44 short:pb-36",
  "false-true": "pb-[204px] short:pb-[172px]",
  "true-false": "pb-56 short:pb-48",
  "true-true": "pb-[252px] short:pb-[220px]",
};

/*
 * One question per screen and one giant button pinned to the bottom with
 * "Not interested" under it. The page's header (Back, stepper) lives in
 * BookIt so it stays put while this slides between steps.
 */
export default function StepShell({
  hero,
  question,
  subtitle,
  action,
  note,
  footnote,
  onDecline,
  children,
}) {
  const dir = useContext(Direction);
  return (
    <>
      <motion.main
        custom={dir}
        variants={slide}
        className={`flex flex-1 flex-col gap-[20px] px-5 pt-4 short:gap-3.5 short:pt-2 ${
          FOOTER_CLEARANCE[`${Boolean(note)}-${Boolean(footnote)}`]
        }`}
      >
        {hero && <motion.div variants={rise}>{hero}</motion.div>}
        <motion.div variants={rise} className="flex flex-col gap-1">
          <h1 className="m-0 text-balance text-[28px] font-bold leading-[1.05] text-slate-900 short:text-[24px]">
            {question}
          </h1>
          {subtitle && (
            <p className="m-0 text-[16px] leading-[1.45] text-slate-600">
              {subtitle}
            </p>
          )}
        </motion.div>
        <motion.div
          variants={rise}
          className="flex flex-col gap-3.5 short:gap-2.5"
        >
          {children}
        </motion.div>
      </motion.main>

      <motion.footer
        variants={fade}
        className="fixed inset-x-0 bottom-0 z-20 bg-gradient-to-t from-slate-50 from-80% to-slate-50/0 px-5 pb-[max(10px,env(safe-area-inset-bottom))] pt-3 short:pt-2"
      >
        <div className="mx-auto flex max-w-[440px] flex-col items-center gap-1">
          {note && (
            <p className="m-0 mb-1 text-balance text-center font-[Roboto] text-[14px] font-medium leading-snug text-amber-800">
              {note}
            </p>
          )}
          {footnote && (
            <p className="m-0 mb-1 font-[Roboto] text-[14px] text-slate-500">
              {footnote}
            </p>
          )}
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              disabled={action.disabled || action.busy}
              className={primaryButton}
            >
              {action.busy ? action.busyLabel || "One moment…" : action.label}
            </button>
          )}
          {onDecline && (
            <button type="button" onClick={onDecline} className={textButton}>
              Not interested in this load
            </button>
          )}
        </div>
      </motion.footer>
    </>
  );
}

export function Brand() {
  return (
    <div className="flex items-center gap-2">
      <img src="/cinesis-logo.png" alt="" className="h-7 w-auto" />
      <span className="font-[Barlow_Condensed] text-[17px] font-bold text-slate-900">
        Cinesis
      </span>
    </div>
  );
}
